import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import AdminHeader from '../../components/AdminHeader';
import { PeriodSelector } from '../../components/AdminPeriodSelector';
import { useAdminPeriod } from '../../components/useAdminPeriod';
import PriceTestOfferRevenue, { type PriceTestRevenueSummary } from './PriceTestOfferRevenue';

type DashboardData = {
  period_days: number;
  overview: {
    total_gerados: number;
    total_pagos: number;
    total_entregues: number;
    total_falha: number;
    abertos: number;
    gmv: number;
    ticket_medio: number;
    pagos_com_cupom: number;
    com_refacao: number;
    com_2_refacao: number;
  };
  receita_por_dia: { dia: string; pedidos: number; receita: number }[];
  top_generos: { label: string; qtd: number }[];
  top_relacoes: { label: string; qtd: number }[];
  top_generos_pagos: { label: string; qtd: number }[];
  top_relacoes_pagos: { label: string; qtd: number }[];
  status_dist: { status: string; qtd: number }[];
  canal: { canal: string; gerados: number; pagos: number; gmv: number }[];
  tempo_entrega_seconds: number;
  top_cupons: { codigo: string; usos: number; gmv: number }[];
  conversas_periodo: number;
  conversas_total: number;
  simple_offer: {
    leads_conversas: number;
    pedidos_pagos: number;
    taxa_conversao_pagamento: number | null;
    vendas_musica_34_90: number;
    vendas_video_49_90: number;
    ticket_medio: number | null;
    receita: number;
    receita_por_lead: number;
    taxa_aprovacao_primeira_previa: number | null;
    refacoes: number;
    pedidos_com_video: number;
    tempo_medio_fotos_entrega_seg: number | null;
    falhas_render: number;
    custo_medio_render_usd: number | null;
    intervencoes_manuais: number;
  } | null;
  price_test: PriceTestRevenueSummary | null;
  recentes: {
    ref_id: string;
    cliente_nome: string | null;
    cliente_phone: string | null;
    nome_homenageado: string | null;
    relacao: string | null;
    status: string;
    valor: number | null;
    criado_em: string;
    source?: string | null;
    offer_id?: string | null;
    offered_cents?: number;
    received_cents?: number;
    rounding_cents?: number;
    capi?: { accepted: boolean; status: string | null; reason: string | null } | null;
    ctwa_clid?: string | null;
    tracking?: {
      tracking_id: string | null;
      utm_source: string | null;
      utm_medium: string | null;
      utm_campaign: string | null;
      utm_campaign_id: string | null;
      utm_adset: string | null;
      utm_adset_id: string | null;
      utm_content: string | null;
      utm_ad_id: string | null;
      utm_placement: string | null;
      fbclid: string | null;
      first_touch_utm: { captured_at?: string; utm_source?: string; utm_campaign?: string; utm_content?: string } | null;
      last_touch_utm: { captured_at?: string; utm_source?: string; utm_campaign?: string; utm_content?: string } | null;
      ad_id?: string | null;
      ad_headline?: string | null;
      ad_name?: string | null;
      campaign_name?: string | null;
    } | null;
  }[];
};

type RecentTracking = NonNullable<DashboardData['recentes'][number]['tracking']>;

const STATUS_COLORS: Record<string, string> = {
  aguardando_pagamento: '#cc6620',
  pago: '#1e6fa8',
  gerando_musica: '#1e6fa8',
  entregando: '#1e6fa8',
  pronto_para_entrega: '#1e6fa8',
  entregue: '#2f7a4a',
  falha_suno: '#a93232',
  falha_entrega: '#a93232',
  falha_geracao_previa: '#a93232',
  cancelado: '#6b6890',
  expirado: '#6b6890',
};

const STATUS_LABELS: Record<string, string> = {
  aguardando_pagamento: 'Aguardando',
  pago: 'Pago',
  gerando_musica: 'Gerando',
  entregando: 'Entregando',
  pronto_para_entrega: 'Pronto',
  entregue: 'Entregue',
  falha_suno: 'Falha (Suno)',
  falha_entrega: 'Falha (entrega)',
  falha_geracao_previa: 'Falha (prévias)',
  cancelado: 'Cancelado',
  expirado: 'Expirado',
};

function formatBRL(v: number | null | undefined): string {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatPct(num: number, den: number): string {
  if (!den) return '—';
  return `${((num / den) * 100).toFixed(1)}%`;
}

function formatRate(value: number | null | undefined): string {
  const n = Number(value);
  return value == null || !Number.isFinite(n) ? '—' : `${n.toFixed(1)}%`;
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m ? `${h}h ${m}min` : `${h}h`;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `há ${Math.floor(diff / 86400)}d`;
  return date.toLocaleDateString('pt-BR');
}

function formatDayLabel(yyyymmdd: string): string {
  const [, m, d] = yyyymmdd.split('-');
  return `${d}/${m}`;
}

function trackingSummary(tracking: DashboardData['recentes'][number]['tracking']): string {
  if (!tracking) return 'Sem UTM capturada';
  const origem = tracking.utm_source || (tracking.ad_id ? 'whatsapp_ctwa' : '');
  const campanha = tracking.utm_campaign || tracking.campaign_name || tracking.utm_campaign_id || '';
  const conjunto = tracking.utm_adset || tracking.utm_adset_id || '';
  const anuncio = tracking.utm_content || tracking.ad_name || tracking.ad_headline || tracking.utm_ad_id || tracking.ad_id || '';
  const posicionamento = tracking.utm_placement || '';
  const parts = [
    origem && `Origem: ${origem}`,
    campanha && `Campanha: ${campanha}`,
    conjunto && `Conjunto: ${conjunto}`,
    anuncio && `Anúncio: ${anuncio}`,
    posicionamento && `Posicionamento: ${posicionamento}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Sem UTM capturada';
}

function touchSummary(label: string, touch: RecentTracking['first_touch_utm'] | undefined | null): string | null {
  if (!touch) return null;
  const source = touch.utm_source || 'origem';
  const campaign = touch.utm_campaign || touch.utm_content || '';
  const when = touch.captured_at ? new Date(touch.captured_at).toLocaleDateString('pt-BR') : '';
  return `${label}: ${[source, campaign, when].filter(Boolean).join(' · ')}`;
}

export default function Dashboard() {
  const { apiQuery } = useAdminPeriod();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`/api/admin-dashboard?${apiQuery}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (err) {
      setError(`Falha ao carregar: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [apiQuery]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const overview = data?.overview;
  const simpleOffer = data?.simple_offer;

  const conversaoPagaPorGerada = useMemo(() => {
    if (!overview) return '—';
    return formatPct(overview.total_pagos, overview.total_gerados);
  }, [overview]);

  const pctCupom = useMemo(() => {
    if (!overview) return '—';
    return formatPct(overview.pagos_com_cupom, overview.total_pagos);
  }, [overview]);

  const pctRefacao = useMemo(() => {
    if (!overview) return '—';
    return formatPct(overview.com_refacao, overview.total_entregues);
  }, [overview]);

  return (
    <div
      className="admin-shell"
      style={{
        minHeight: '100vh',
        background: 'var(--cream)',
        color: 'var(--text-body)',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AdminHeader title="Dashboard" />

      <main style={{ flex: 1, padding: '20px 16px 60px', maxWidth: 1280, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: 'var(--heading-ink)',
            }}
          >
            Visão geral
          </h1>
          <PeriodSelector />
        </div>

        {loading && !data && <SkeletonGrid />}

        {error && (
          <div
            style={{
              padding: 20,
              borderRadius: 12,
              background: 'rgba(220,80,80,0.08)',
              border: '1px solid rgba(220,80,80,0.30)',
              color: '#a93232',
              textAlign: 'center',
              marginBottom: 20,
            }}
          >
            <p style={{ margin: '0 0 12px', fontSize: 14 }}>{error}</p>
            <button
              onClick={() => {
                setLoading(true);
                load();
              }}
              style={{
                padding: '8px 16px',
                borderRadius: 999,
                border: 'none',
                background: 'var(--gold)',
                color: 'var(--gold-ink)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Tentar novamente
            </button>
          </div>
        )}

        {data && (
          <>
            <CardGrid>
              <KpiCard
                label="Receita"
                value={formatBRL(overview?.gmv)}
                hint={`${overview?.total_pagos ?? 0} pagamentos`}
                accent="green"
                accentGold
              />
              <KpiCard
                label="Pedidos pagos"
                value={String(overview?.total_pagos ?? 0)}
                hint={`${overview?.total_gerados ?? 0} gerados · ${conversaoPagaPorGerada} conversão`}
              />
              <KpiCard
                label="Ticket médio"
                value={formatBRL(overview?.ticket_medio)}
              />
              <KpiCard
                label="Conversas iniciadas"
                value={String(data.conversas_periodo ?? 0)}
                hint={`${data.conversas_total ?? 0} no histórico`}
              />
              <KpiCard
                label="Em andamento agora"
                value={String(overview?.abertos ?? 0)}
                hint="aguardando pgto, gerando, entregando"
                accent="orange"
              />
            </CardGrid>

            <PriceTestOfferRevenue data={data.price_test} />

            <SectionTitle>Experimento · oferta simples v17</SectionTitle>
            <CardGrid>
              <KpiCard
                label="Conversão para pagamento"
                value={simpleOffer ? formatRate(simpleOffer.taxa_conversao_pagamento) : '—'}
                hint={simpleOffer ? `${simpleOffer.pedidos_pagos} pagos em ${simpleOffer.leads_conversas} conversas` : 'Sem pedidos v17 no período'}
              />
              <KpiCard label="Música · R$ 34,90" value={String(simpleOffer?.vendas_musica_34_90 ?? 0)} hint="Vendas pagas" />
              <KpiCard
                label="Música + vídeo · R$ 49,90"
                value={String(simpleOffer?.vendas_video_49_90 ?? 0)}
                hint="Vendas pagas"
                accent="orange"
              />
              <KpiCard
                label="Aprovação da 1ª prévia"
                value={simpleOffer ? formatRate(simpleOffer.taxa_aprovacao_primeira_previa) : '—'}
                hint={`${simpleOffer?.refacoes ?? 0} refações`}
              />
              <KpiCard
                label="Render de vídeo"
                value={simpleOffer ? formatDuration(Number(simpleOffer.tempo_medio_fotos_entrega_seg)) : '—'}
                hint={`${simpleOffer?.falhas_render ?? 0} falhas · custo médio US$ ${Number(simpleOffer?.custo_medio_render_usd || 0).toFixed(4)}`}
                accent={simpleOffer && simpleOffer.falhas_render > 0 ? 'red' : undefined}
              />
            </CardGrid>

            <CardGrid mt={16}>
              <KpiCard label="% vendas com cupom" value={pctCupom} hint={`${overview?.pagos_com_cupom ?? 0} pedidos`} small />
              <KpiCard label="% com refação" value={pctRefacao} hint={`${overview?.com_refacao ?? 0} de ${overview?.total_entregues ?? 0} entregues`} small />
              <KpiCard
                label="2ª refação"
                value={String(overview?.com_2_refacao ?? 0)}
                hint="aprovadas pelo admin"
                small
              />
              <KpiCard
                label="Tempo médio entrega"
                value={formatDuration(Number(data.tempo_entrega_seconds))}
                hint="pago → entregue"
                small
              />
              <KpiCard
                label="Entregues"
                value={String(overview?.total_entregues ?? 0)}
                hint={`${overview?.total_falha ?? 0} com falha`}
                small
                accent={overview && overview.total_falha > 0 ? 'red' : undefined}
              />
            </CardGrid>

            <SectionTitle>Receita por dia</SectionTitle>
            <Card>
              {data.receita_por_dia.length === 0 ? (
                <EmptyMsg>Sem pagamentos no período.</EmptyMsg>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.receita_por_dia} margin={{ top: 8, right: 8, bottom: 8, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-fade)" />
                    <XAxis dataKey="dia" tickFormatter={formatDayLabel} fontSize={11} stroke="var(--text-muted)" />
                    <YAxis fontSize={11} stroke="var(--text-muted)" />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--surface-white)',
                        border: '1px solid var(--border-soft)',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                      labelFormatter={(v) => formatDayLabel(String(v))}
                      formatter={(v: number, n: string) => [n === 'receita' ? formatBRL(v) : v, n === 'receita' ? 'Receita' : 'Pedidos']}
                    />
                    <Line type="monotone" dataKey="receita" stroke="var(--gold)" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Two>
              <div>
                <SectionTitle>Top gêneros · pedidos feitos</SectionTitle>
                <Card>
                  {data.top_generos.length === 0 ? (
                    <EmptyMsg>Sem dados.</EmptyMsg>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(220, data.top_generos.length * 32)}>
                      <BarChart data={data.top_generos} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                        <XAxis type="number" fontSize={11} stroke="var(--text-muted)" />
                        <YAxis type="category" dataKey="label" width={100} fontSize={11} stroke="var(--text-muted)" />
                        <Tooltip
                          contentStyle={{ background: 'var(--surface-white)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 13 }}
                        />
                        <Bar dataKey="qtd" fill="var(--gold)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>
              <div>
                <SectionTitle>Top relações · pedidos feitos</SectionTitle>
                <Card>
                  {data.top_relacoes.length === 0 ? (
                    <EmptyMsg>Sem dados.</EmptyMsg>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(220, data.top_relacoes.length * 32)}>
                      <BarChart data={data.top_relacoes} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                        <XAxis type="number" fontSize={11} stroke="var(--text-muted)" />
                        <YAxis type="category" dataKey="label" width={120} fontSize={11} stroke="var(--text-muted)" />
                        <Tooltip
                          contentStyle={{ background: 'var(--surface-white)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 13 }}
                        />
                        <Bar dataKey="qtd" fill="var(--terra)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>
            </Two>

            <Two>
              <div>
                <SectionTitle>Top gêneros · compras realizadas</SectionTitle>
                <Card>
                  {data.top_generos_pagos.length === 0 ? (
                    <EmptyMsg>Nenhuma compra no período.</EmptyMsg>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(220, data.top_generos_pagos.length * 32)}>
                      <BarChart data={data.top_generos_pagos} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                        <XAxis type="number" fontSize={11} stroke="var(--text-muted)" />
                        <YAxis type="category" dataKey="label" width={100} fontSize={11} stroke="var(--text-muted)" />
                        <Tooltip
                          contentStyle={{ background: 'var(--surface-white)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 13 }}
                        />
                        <Bar dataKey="qtd" fill="#2f7a4a" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>
              <div>
                <SectionTitle>Top relações · compras realizadas</SectionTitle>
                <Card>
                  {data.top_relacoes_pagos.length === 0 ? (
                    <EmptyMsg>Nenhuma compra no período.</EmptyMsg>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(220, data.top_relacoes_pagos.length * 32)}>
                      <BarChart data={data.top_relacoes_pagos} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                        <XAxis type="number" fontSize={11} stroke="var(--text-muted)" />
                        <YAxis type="category" dataKey="label" width={120} fontSize={11} stroke="var(--text-muted)" />
                        <Tooltip
                          contentStyle={{ background: 'var(--surface-white)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 13 }}
                        />
                        <Bar dataKey="qtd" fill="#1e6fa8" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>
            </Two>

            <Two>
              <div>
                <SectionTitle>Distribuição por status</SectionTitle>
                <Card>
                  {data.status_dist.length === 0 ? (
                    <EmptyMsg>Sem dados.</EmptyMsg>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {data.status_dist.map((s) => {
                        const max = Math.max(...data.status_dist.map((x) => x.qtd));
                        const w = max ? (s.qtd / max) * 100 : 0;
                        return (
                          <div key={s.status} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 110, fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
                              {STATUS_LABELS[s.status] || s.status}
                            </div>
                            <div style={{ flex: 1, height: 14, background: 'var(--badge-bg)', borderRadius: 999, overflow: 'hidden' }}>
                              <div
                                style={{
                                  width: `${w}%`,
                                  height: '100%',
                                  background: STATUS_COLORS[s.status] || 'var(--gold)',
                                  transition: 'width 0.4s',
                                }}
                              />
                            </div>
                            <div style={{ width: 36, textAlign: 'right', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
                              {s.qtd}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </div>
              <div>
                <SectionTitle>Canal de origem</SectionTitle>
                <Card>
                  {data.canal.length === 0 ? (
                    <EmptyMsg>Sem dados.</EmptyMsg>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.06 }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px' }}>Canal</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Gerados</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Pagos</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Receita</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.canal.map((c) => (
                          <tr key={c.canal} style={{ borderTop: '1px solid var(--border-fade)' }}>
                            <td style={{ padding: '8px', fontWeight: 700, color: 'var(--heading-ink)' }}>{c.canal}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{c.gerados}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{c.pagos}</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{formatBRL(c.gmv)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
              </div>
            </Two>

            <Two>
              <div>
                <SectionTitle>Top cupons</SectionTitle>
                <Card>
                  {data.top_cupons.length === 0 ? (
                    <EmptyMsg>Nenhum cupom usado no período.</EmptyMsg>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.06 }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px' }}>Cupom</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Usos</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px' }}>Receita</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_cupons.map((c) => (
                          <tr key={c.codigo} style={{ borderTop: '1px solid var(--border-fade)' }}>
                            <td style={{ padding: '8px', fontWeight: 700, color: 'var(--heading-ink)' }}>{c.codigo}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{c.usos}</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{formatBRL(c.gmv)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
              </div>
              <div>
                <SectionTitle>Últimos pedidos</SectionTitle>
                <Card>
                  {data.recentes.length === 0 ? (
                    <EmptyMsg>Nenhum pedido.</EmptyMsg>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {data.recentes.map((p) => (
                        <li
                          key={p.ref_id}
                          style={{
                            display: 'flex',
                            gap: 10,
                            padding: '10px 4px',
                            borderTop: '1px solid var(--border-fade)',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: 'var(--heading-ink)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {p.cliente_nome || p.cliente_phone || '(sem nome)'}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {p.source === 'price_test' ? `Oferta ${p.offer_id === 'music_1990' ? 'R$ 19,90' : p.offer_id === 'music_990' ? 'R$ 9,90' : '990/1990'} · ` : ''}
                              Para {p.nome_homenageado || '?'}
                              {p.relacao ? ` (${p.relacao})` : ''} · {formatRelative(p.criado_em)}
                            </div>
                            <div
                              title={[
                                trackingSummary(p.tracking),
                                p.tracking?.tracking_id ? `tracking_id: ${p.tracking.tracking_id}` : '',
                                touchSummary('First touch', p.tracking?.first_touch_utm),
                                touchSummary('Last touch', p.tracking?.last_touch_utm),
                              ].filter(Boolean).join('\n')}
                              style={{
                                marginTop: 4,
                                fontSize: 10.5,
                                color: p.tracking ? 'var(--text-strong)' : 'var(--text-soft)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {trackingSummary(p.tracking)}
                              {p.tracking?.tracking_id ? ` · ${p.tracking.tracking_id}` : ''}
                            </div>
                          </div>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 10,
                              fontWeight: 700,
                              background: 'var(--badge-bg)',
                              color: STATUS_COLORS[p.status] || 'var(--text-muted)',
                              border: '1px solid var(--border-soft)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {STATUS_LABELS[p.status] || p.status}
                          </span>
                          <span
                            style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-strong)', whiteSpace: 'nowrap' }}
                          >
                            {formatBRL(p.valor)}
                            {p.source === 'price_test' && p.offered_cents != null && <small style={{ display: 'block', fontWeight: 400, color: 'var(--text-muted)', marginTop: 3 }}>Oferta {formatBRL(p.offered_cents / 100)}{Boolean(p.rounding_cents) && ` · diferença ${formatBRL(p.rounding_cents! / 100)}`}</small>}
                            {p.source === 'price_test' && p.capi && <small style={{ display: 'block', fontWeight: 400, color: 'var(--text-muted)', marginTop: 3 }}>{p.capi.accepted ? 'Meta confirmou' : p.capi.reason === 'no_ctwa_clid' ? 'Sem clique CAPI' : 'CAPI pendente'}</small>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            </Two>
          </>
        )}
      </main>
    </div>
  );
}

// ----- Sub-componentes -----

function CardGrid({ children, mt = 0 }: { children: React.ReactNode; mt?: number }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginTop: mt,
      }}
    >
      {children}
    </div>
  );
}

function Two({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
        marginTop: 24,
      }}
    >
      {children}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="admin-glass"
      style={{
        background: 'var(--surface-white)',
        borderRadius: 18,
        padding: 16,
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontSize: 14,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: 'var(--heading-ink)',
        margin: '24px 0 8px',
      }}
    >
      {children}
    </h2>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
      {children}
    </p>
  );
}

function KpiCard({
  label,
  value,
  hint,
  accent,
  small,
  accentGold,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: 'green' | 'orange' | 'red';
  small?: boolean;
  accentGold?: boolean;
}) {
  const accentColor =
    accent === 'green' ? 'var(--kpi-revenue)' : accent === 'orange' ? '#cc6620' : accent === 'red' ? '#a93232' : 'var(--heading-ink)';
  return (
    <div
      className={accentGold ? 'admin-glass admin-accent-gold' : 'admin-glass'}
      style={{
        background: 'var(--surface-white)',
        borderRadius: 18,
        padding: small ? '13px 15px' : '17px 19px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minHeight: small ? 70 : 92,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: small ? 22 : 28,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: accentColor,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</div>}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
      }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            height: 92,
            background: 'var(--badge-bg)',
            borderRadius: 18,
            border: '1px solid var(--border-soft)',
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  );
}
